/**
 * AP-29-E — Der Paket-Katalog des Betreibers.
 *
 * Pakete stehen in der **Datenbank, nicht im Code**: Der Betreiber stellt sie
 * hier aus Modulen (und aus anderen Paketen) zusammen und schneidet sie jederzeit
 * neu, ohne dass jemand ein Deployment braucht.
 *
 * Der Satz, der die ganze Seite trägt und deshalb auch darauf steht: **Eine
 * Änderung am Paket ändert nichts an dem, was ein Mandant schon gebucht hat.**
 * Gebucht wird immer je Modul; das Paket ist eine Vorauswahl und hinterlässt an
 * der Buchung nur seine Herkunft. Ohne diese Zusicherung wäre jede Umstellung
 * hier ein Eingriff in laufende Verträge.
 *
 * Bewusst keine Listen-Standard-Seite: ein kurzer, handgepflegter Katalog, keine
 * Auswertung, die man filtert und exportiert.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from '@symphenia/ui-components'
import { geldMitWaehrung } from '@symphenia/shared-types'
import { ArrowLeft, Package, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react'
import {
  MODULE_REGISTRY,
  componentsOf,
  isBookable,
  isModuleGroup,
  withDependencies,
} from '@symphenia/shared-types'
import { useTranslation } from 'react-i18next'
import { moduleLabel } from '@/lib/module-label'
import { formCard, pageWide } from '@/lib/form-styles'
import { operatorApi, type ModulePackage, type CatalogRow } from '../api/operator.api'

/** Nur was sich verkaufen lässt, gehört in ein Angebot (Reifegrad in der Registry). */
const VERKAUFBAR = MODULE_REGISTRY.filter((m) => isBookable(m.key)).map((m) => m.key)

/**
 * AP-29-N-F: Die zerlegten Module mit ihren Bausteinen — die Staffelung der
 * Maske. Ohne sie stünden hier bald vierzig gleichrangige Häkchen, und niemand
 * sähe mehr, was zusammengehört.
 */
const GRUPPEN = MODULE_REGISTRY.filter((m) => isModuleGroup(m.key)).map((g) => ({
  key: g.key,
  name: g.key,
  teile: componentsOf(g.key).filter((t) => isBookable(t.key)),
}))

/** Module ohne Zerlegung — sie bleiben eine einzelne Zeile. */
const EINZELMODULE = VERKAUFBAR.filter((k) => !MODULE_REGISTRY.find((m) => m.key === k)?.parent)

/**
 * Die Vertriebsbezeichnungen aus den Katalogzeilen — in der Form, die
 * `moduleLabel` erwartet. Eine Zeile ohne Betrag ist hier der Normalfall: Sie
 * traegt dann nur den Namen.
 */
function bezeichnungenAus(
  preise: Map<string, CatalogRow>,
): Readonly<Record<string, string | null>> {
  return Object.fromEntries([...preise].map(([k, p]) => [k, p.salesName]))
}

/** Ein Betrag in der Schreibweise von Symphenia (`geld.ts`). */
const geld = geldMitWaehrung

/** Kurzform eines Preises, wie sie neben einem Häkchen steht. */
function preisText(key: string, preise: Map<string, CatalogRow>): string | null {
  const p = preise.get(key)
  // Eine Katalogzeile ohne Betrag traegt nur die Bezeichnung — kein Preis.
  if (!p || p.amount === null) return null
  const betrag = geldMitWaehrung(p.amount, p.currency)
  const bezug = p.basis === 'PER_SEAT' ? '/Platz' : ''
  const zeitraum = p.period === 'YEAR' ? '/Jahr' : '/Monat'
  return `${betrag}${bezug}${zeitraum}`
}

type Entwurf = {
  key: string
  name: string
  description: string
  moduleKeys: string[]
  packageKeys: string[]
  isActive: boolean
  sortOrder: number
}

const leererEntwurf = (): Entwurf => ({
  key: '',
  name: '',
  description: '',
  moduleKeys: [],
  packageKeys: [],
  isActive: true,
  sortOrder: 0,
})

function meldung(e: unknown, ersatz: string): string {
  const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
    ?.message
  return Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? ersatz)
}

export default function OperatorPackagesPage() {
  const [pakete, setPakete] = useState<ModulePackage[]>([])
  /** Preise je Baustein bzw. Paket — fuer die Kurzform neben den Haekchen. */
  const [preise, setPreise] = useState<Map<string, CatalogRow>>(new Map())
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const bezeichnungen = useMemo(() => bezeichnungenAus(preise), [preise])
  // `null` = geschlossen, Entwurf ohne `bestand` = neu, mit = ändern.
  const [dialog, setDialog] = useState<{ entwurf: Entwurf; bestand: ModulePackage | null } | null>(
    null,
  )

  const laden = useCallback(async () => {
    setLoading(true)
    try {
      // Auch die stillgelegten: Sie stehen im Katalog, nur nicht im Verkauf.
      const [geladen, preisliste] = await Promise.all([
        operatorApi.listPackages(true),
        // Ein fehlender Preis ist kein Fehler ("noch nicht bepreist"), also darf
        // eine leere Preisliste den Katalog nicht aufhalten.
        operatorApi.listCatalog().catch(() => [] as CatalogRow[]),
      ])
      setPakete(geladen)
      setPreise(new Map(preisliste.map((p) => [p.itemKey, p])))
      setFehler(null)
    } catch (e) {
      setFehler(meldung(e, 'Der Paket-Katalog konnte nicht geladen werden.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const loeschen = async (paket: ModulePackage) => {
    setFehler(null)
    try {
      await operatorApi.deletePackage(paket.key)
      await laden()
    } catch (e) {
      // Der Server sagt konkret, warum nicht („steht als Herkunft an 3 Buchungen").
      setFehler(meldung(e, 'Das Paket konnte nicht gelöscht werden.'))
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <Link
          to="/admin/mandanten"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Mandanten
        </Link>
        {' '}
        <Link
          to="/admin/preise"
          className="mb-2 ml-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          Preise pflegen
        </Link>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
              <Package size={26} className="text-primary" /> Pakete
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Verkaufsbündel aus Modulen — frei zusammenstellbar und jederzeit änderbar. Eine
              Änderung hier wirkt <strong>nicht</strong> rückwirkend: Gebucht wird je Modul, das
              Paket hinterlässt an der Buchung nur seine Herkunft.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDialog({ entwurf: leererEntwurf(), bestand: null })}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <Plus size={16} /> Paket anlegen
          </button>
        </div>
      </div>

      {fehler && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Wird geladen…</p>
      ) : pakete.length === 0 ? (
        <div className={`${formCard} text-sm text-muted-foreground`}>
          Noch kein Paket angelegt. Module lassen sich auch einzeln buchen — ein Paket spart nur
          die Wiederholung.
        </div>
      ) : (
        <div className="space-y-3">
          {pakete.map((p) => (
            <PaketKarte
              key={p.key}
              paket={p}
              onAendern={() =>
                setDialog({
                  entwurf: {
                    key: p.key,
                    name: p.name,
                    description: p.description ?? '',
                    moduleKeys: p.moduleKeys,
                    packageKeys: p.packageKeys,
                    isActive: p.isActive,
                    sortOrder: p.sortOrder,
                  },
                  bestand: p,
                })
              }
              namen={bezeichnungen}
              onLoeschen={() => void loeschen(p)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <PaketDialog
          entwurf={dialog.entwurf}
          bestand={dialog.bestand}
          alle={pakete}
          preise={preise}
          onClose={() => setDialog(null)}
          onFertig={() => {
            setDialog(null)
            void laden()
          }}
        />
      )}
    </div>
  )
}

// ─── Eine Paketzeile ──────────────────────────────────────────────────────────

function PaketKarte({
  paket,
  namen,
  onAendern,
  onLoeschen,
}: {
  paket: ModulePackage
  /** AP-29-N: Vertriebsbezeichnungen aus dem Katalog — siehe `moduleLabel`. */
  namen: Readonly<Record<string, string | null | undefined>>
  onAendern: () => void
  onLoeschen: () => void
}) {
  const { t: uebersetze } = useTranslation()
  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-foreground">{paket.name}</h2>
            <span className="font-mono text-xs text-muted-foreground">{paket.key}</span>
            {!paket.isActive && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                nicht im Verkauf
              </span>
            )}
          </div>
          {paket.description && (
            <p className="mt-1 text-sm text-muted-foreground">{paket.description}</p>
          )}

          <p className="mt-2 text-sm text-foreground">
            {paket.resolvedModuleKeys.length === 0 ? (
              <span className="text-muted-foreground">Enthält noch kein buchbares Modul.</span>
            ) : (
              <>
                <span className="text-muted-foreground">Ergibt beim Buchen:</span>{' '}
                {paket.resolvedModuleKeys.map((k) => moduleLabel(k, uebersetze, namen)).join(', ')}
              </>
            )}
          </p>
          {paket.packageKeys.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Enthält die Pakete: {paket.packageKeys.join(', ')}
            </p>
          )}

          {/*
            AP-29-N-I: Festsetzung und Summe der Teile nebeneinander. Beide
            Zahlen, damit der Betreiber sieht, wie viel Buendelrabatt er gerade
            gibt — und bei welchen Bausteinen noch gar kein Preis steht.
          */}
          {paket.pricing &&
            (paket.pricing.price?.amount != null || paket.pricing.sumOfParts !== null) && (
            <p className="mt-1 text-xs text-muted-foreground">
              {paket.pricing.price && paket.pricing.price.amount !== null ? (
                <>
                  <span className="font-medium text-foreground">
                    {geld(paket.pricing.price.amount, paket.pricing.price.currency)}
                    {paket.pricing.price.basis === 'PER_SEAT' ? '/Platz' : ''}
                    {paket.pricing.price.period === 'YEAR' ? '/Jahr' : '/Monat'}
                  </span>
                  {paket.pricing.sumOfParts !== null && (
                    <> — einzeln {geld(paket.pricing.sumOfParts, paket.pricing.price.currency)}</>
                  )}
                </>
              ) : (
                <>Kein Paketpreis — einzeln {geld(paket.pricing.sumOfParts ?? 0, 'EUR')}</>
              )}
              {paket.pricing.unpriced.length > 0 && (
                <> · ohne Preis: {paket.pricing.unpriced.map((k) => moduleLabel(k, uebersetze, namen)).join(', ')}</>
              )}
            </p>
          )}

          {/* Was sich nicht mehr auflösen lässt, steht hier — statt beim Buchen
              zu überraschen. */}
          {paket.problems.length > 0 && (
            <p className="mt-2 inline-flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>Wird übersprungen: {paket.problems.join(' · ')}</span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onAendern}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <Pencil size={13} /> Ändern
          </button>
          <button
            type="button"
            onClick={onLoeschen}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={13} /> Löschen
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Zusammenstellen ──────────────────────────────────────────────────────────

function PaketDialog({
  entwurf: start,
  bestand,
  alle,
  preise,
  onClose,
  onFertig,
}: {
  entwurf: Entwurf
  bestand: ModulePackage | null
  alle: ModulePackage[]
  preise: Map<string, CatalogRow>
  onClose: () => void
  onFertig: () => void
}) {
  const [e, setE] = useState<Entwurf>(start)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const { t: uebersetze } = useTranslation()

  /** Die Vertriebsbezeichnungen aus dem Katalog — sie schlagen die i18n-Vorgabe. */
  const bezeichnungen = useMemo(() => bezeichnungenAus(preise), [preise])

  const setzen = (teil: Partial<Entwurf>) => setE((prev) => ({ ...prev, ...teil }))

  const umschalten = (feld: 'moduleKeys' | 'packageKeys', wert: string) =>
    setE((prev) => ({
      ...prev,
      [feld]: prev[feld].includes(wert)
        ? prev[feld].filter((k) => k !== wert)
        : [...prev[feld], wert],
    }))

  // Dieselbe Auflösung wie im Backend, nur eine Ebene tief vorgerechnet: Der
  // Betreiber soll beim Zusammenstellen sehen, was herauskommt.
  const vorschau = useMemo(() => {
    const module = new Set(e.moduleKeys)
    for (const k of e.packageKeys) {
      alle.find((p) => p.key === k)?.resolvedModuleKeys.forEach((m) => module.add(m))
    }
    return [...module].sort()
  }, [e.moduleKeys, e.packageKeys, alle])

  /** Ein ganzes Modul auf einmal an- oder abhaken. */
  const gruppeUmschalten = (teile: string[], anhaken: boolean) =>
    setE((prev) => ({
      ...prev,
      moduleKeys: anhaken
        ? [...new Set([...prev.moduleKeys, ...teile])]
        : prev.moduleKeys.filter((k) => !teile.includes(k)),
    }))

  /**
   * Was der Server beim Speichern ergänzen wird (AP-29-N-C).
   *
   * Eine fehlende Voraussetzung ist keine Fehleingabe — abgelehnt wird sie
   * nicht. Ungesagt wäre sie aber eine Überraschung: Im Katalog stünde dann
   * etwas anderes, als der Betreiber angehakt hat.
   */
  const fehlendeVoraussetzungen = useMemo(
    () => withDependencies(e.moduleKeys).filter((k) => !e.moduleKeys.includes(k)),
    [e.moduleKeys],
  )

  // Ein Paket kann sich nicht selbst enthalten — der Server lehnt es ab, also
  // steht es hier gar nicht erst zur Auswahl.
  const waehlbarePakete = alle.filter((p) => p.key !== e.key)

  const gueltig = e.name.trim().length >= 2 && (bestand !== null || e.key.trim().length >= 2)

  const speichern = async () => {
    setSpeichert(true)
    setFehler(null)
    try {
      const rumpf = {
        name: e.name.trim(),
        description: e.description.trim(),
        moduleKeys: e.moduleKeys,
        packageKeys: e.packageKeys,
        isActive: e.isActive,
        sortOrder: e.sortOrder,
      }
      if (bestand) await operatorApi.updatePackage(bestand.key, rumpf)
      else await operatorApi.createPackage({ ...rumpf, key: e.key.trim() })
      onFertig()
    } catch (err) {
      setFehler(meldung(err, 'Speichern fehlgeschlagen.'))
      setSpeichert(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={bestand ? `${bestand.name} ändern` : 'Paket anlegen'}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void speichern()}
            disabled={speichert || !gueltig}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {speichert ? 'Wird gespeichert…' : 'Speichern'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Name</span>
            <input
              value={e.name}
              onChange={(ev) => setzen({ name: ev.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Schlüssel</span>
            <input
              value={e.key}
              disabled={bestand !== null}
              onChange={(ev) => setzen({ key: ev.target.value.toUpperCase() })}
              placeholder="HANDWERK_KOMPLETT"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm disabled:opacity-60"
            />
            {/* Der Schlüssel steht als Herkunft an jeder Buchung, die je über
                dieses Paket entstanden ist. */}
            <span className="mt-1 block text-xs text-muted-foreground">
              {bestand ? 'Bleibt, wie er ist — er steht an bestehenden Buchungen.' : 'Später nicht mehr änderbar.'}
            </span>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-foreground">Beschreibung</span>
          <textarea
            rows={2}
            value={e.description}
            onChange={(ev) => setzen({ description: ev.target.value })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-foreground">Bausteine</legend>

          {/*
            AP-29-N-F: Nach Gruppe gestaffelt statt flach. Ohne das staenden hier
            bald vierzig gleichrangige Haekchen, und niemand saehe mehr, was
            zusammengehoert. Das Haekchen der Gruppe ist die Abkuerzung fuer
            "alles davon" — das volle Paket soll ein Klick sein, nicht sechs.
          */}
          <div className="space-y-3">
            {GRUPPEN.map((g) => {
              const teile = g.teile.map((t) => t.key)
              const alleDrin = teile.every((k) => e.moduleKeys.includes(k))
              const einigeDrin = !alleDrin && teile.some((k) => e.moduleKeys.includes(k))

              return (
                <div key={g.key} className="rounded-md border border-border p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <input
                      type="checkbox"
                      checked={alleDrin}
                      ref={(el) => {
                        // Teilauswahl sichtbar machen: Ein halb gefuelltes Paket
                        // sieht sonst aus wie ein leeres.
                        if (el) el.indeterminate = einigeDrin
                      }}
                      onChange={() => gruppeUmschalten(teile, !alleDrin)}
                      className="h-4 w-4"
                    />
                    {moduleLabel(g.key, uebersetze, bezeichnungen)}
                    <span className="font-normal text-muted-foreground">
                      — ganzes Modul ({teile.length} Teile)
                    </span>
                  </label>

                  <div className="mt-2 grid gap-1.5 pl-6 sm:grid-cols-2">
                    {g.teile.map((t) => (
                      <label key={t.key} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={e.moduleKeys.includes(t.key)}
                          onChange={() => umschalten('moduleKeys', t.key)}
                          className="h-4 w-4"
                        />
                        {moduleLabel(t.key, uebersetze, bezeichnungen)}
                        {preisText(t.key, preise) && (
                          <span className="text-xs text-muted-foreground">
                            {preisText(t.key, preise)}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}

            {EINZELMODULE.length > 0 && (
              <div className="grid gap-1.5 sm:grid-cols-2">
                {EINZELMODULE.map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={e.moduleKeys.includes(key)}
                      onChange={() => umschalten('moduleKeys', key)}
                      className="h-4 w-4"
                    />
                    {moduleLabel(key, uebersetze, bezeichnungen)}
                    {preisText(key, preise) && (
                      <span className="text-xs text-muted-foreground">{preisText(key, preise)}</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/*
            AP-29-N-C: Eine fehlende Voraussetzung ist keine Fehleingabe — der
            Server ergaenzt sie beim Speichern. Ungesagt waere sie aber eine
            Ueberraschung: Im Katalog staende dann etwas anderes, als der
            Betreiber angehakt hat.
          */}
          {fehlendeVoraussetzungen.length > 0 && (
            <p className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {fehlendeVoraussetzungen.join(', ')} {fehlendeVoraussetzungen.length === 1 ? 'wird' : 'werden'}{' '}
                beim Speichern ergänzt — die angehakten Bausteine setzen{' '}
                {fehlendeVoraussetzungen.length === 1 ? 'ihn' : 'sie'} voraus.
              </span>
            </p>
          )}
        </fieldset>

        {waehlbarePakete.length > 0 && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-foreground">
              Enthaltene Pakete
              <span className="ml-2 font-normal text-muted-foreground">
                — ihre Module kommen dazu
              </span>
            </legend>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {waehlbarePakete.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={e.packageKeys.includes(p.key)}
                    onChange={() => umschalten('packageKeys', p.key)}
                    className="h-4 w-4"
                  />
                  {p.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={e.isActive}
              onChange={(ev) => setzen({ isActive: ev.target.checked })}
              className="h-4 w-4"
            />
            Im Verkauf
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground">
            Reihenfolge
            <input
              type="number"
              min={0}
              value={e.sortOrder}
              onChange={(ev) => setzen({ sortOrder: Number(ev.target.value) || 0 })}
              className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
          </label>
        </div>

        {/* Die Vorschau ist der Punkt der ganzen Maske: Was am Ende beim Kunden
            ankommt, sind Module — nicht das Paket. */}
        <div className="rounded-md bg-muted/40 px-4 py-3 text-sm">
          <span className="font-medium text-foreground">Ergibt beim Buchen:</span>{' '}
          {vorschau.length ? (
            <span className="text-foreground">{vorschau.join(', ')}</span>
          ) : (
            <span className="text-muted-foreground">noch nichts</span>
          )}
        </div>

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}
